/**
 * Plotly renderer — supports ALL chart types (2D, 2.5D, 3D, polar, statistical).
 */

import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import Plot from 'react-plotly.js';
import type { ChartRendererProps } from '../types';
import { PALETTES, createDefaultMapping } from '../types';
import {
  resolveColorMapping,
  resolveSizeMapping,
  resolveShapeMapping,
  isClAlphaAxes,
} from '../visualMappingUtils';
import { useProvenanceContext } from '../../../context/ProvenanceContext';
import { useDataPointContextMenu, DataPointContextMenu } from '../../shared/DataPointContextMenu';

const FLASH_STYLE = `
@keyframes flash-point {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
`;

const MINI_FONT_SIZE = 8;
const MINI_MARGIN = { t: 4, r: 4, b: 18, l: 24 };
const MINI_DENSE_CHARTS = new Set(['contour', 'heatmap', 'surface3d', 'scatter3d']);

function getPalette(name: string): string[] {
  return PALETTES[name] ?? PALETTES['FlexCompute']!;
}

function getColor(palette: string[], _key: string | undefined, idx: number): string {
  return palette[idx % palette.length]!;
}

function groupData(data: Record<string, unknown>[], groupBy?: string) {
  if (!groupBy) return new Map([['All', data]]);
  const groups = new Map<string, Record<string, unknown>[]>();
  for (const row of data) {
    const key = String(row[groupBy] ?? 'Unknown');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }
  return groups;
}

function linearRegression(xs: number[], ys: number[]): { slope: number; intercept: number } {
  const n = xs.length;
  const sx = xs.reduce((a, b) => a + b, 0);
  const sy = ys.reduce((a, b) => a + b, 0);
  const sxy = xs.reduce((a, x, i) => a + x * ys[i]!, 0);
  const sx2 = xs.reduce((a, x) => a + x * x, 0);
  const slope = (n * sxy - sx * sy) / (n * sx2 - sx * sx) || 0;
  const intercept = (sy - slope * sx) / n;
  return { slope, intercept };
}

function computeLocalGradients(xs: number[], ys: number[]): number[] {
  const n = xs.length;
  if (n < 2) return xs.map(() => NaN);
  const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => xs[a]! - xs[b]!);
  const g = new Array<number>(n);
  for (let si = 0; si < n; si++) {
    const i = order[si]!;
    if (si === 0) {
      const j = order[1]!;
      g[i] = (xs[j]! - xs[i]!) ? (ys[j]! - ys[i]!) / (xs[j]! - xs[i]!) : NaN;
    } else if (si === n - 1) {
      const j = order[si - 1]!;
      g[i] = (xs[i]! - xs[j]!) ? (ys[i]! - ys[j]!) / (xs[i]! - xs[j]!) : NaN;
    } else {
      const prev = order[si - 1]!;
      const next = order[si + 1]!;
      g[i] = (xs[next]! - xs[prev]!) ? (ys[next]! - ys[prev]!) / (xs[next]! - xs[prev]!) : NaN;
    }
  }
  return g;
}

function mergedNumericFieldsFromData(data: Record<string, unknown>[]): string[] {
  if (data.length === 0) return [];
  const first = data[0]!;
  return Object.keys(first).filter((k) => typeof first[k] === 'number' && k !== 'id');
}

const PLOTLY_MARKER_MAP: Record<string, string> = {
  circle: 'circle', square: 'square', diamond: 'diamond',
  triangle: 'triangle-up', cross: 'cross', x: 'x',
};

export function PlotlyRenderer({
  data: rawData,
  config,
  theme,
  mini = false,
  width,
  height,
  onPointClick,
  selectedNodeId,
}: ChartRendererProps) {
  const { selectNodes, clearSelection } = useProvenanceContext();
  const ctxMenu = useDataPointContextMenu(rawData);
  const {
    chartType, xAxis, yAxis, zAxis, groupBy, title, showLegend, showGrid,
    markerSize, markerShape, markerOpacity, lineStyle, lineWidth,
    barMode, barOpacity, histBins,
    palette, colorScale, invertColorScale,
    trendLine, showMean, showMedian, showDataLabels, showLiftCurveSlope,
    legendPosition, plotPadding, titleSize, titleWeight,
    contourLevels, contourSmooth,
    xAxisTickAngle, yAxisTickAngle,
  } = config;
  const miniWidth = Math.max(width ?? 180, 1);
  const miniHeight = Math.max(height ?? 120, 1);
  const denseMiniChart = mini && MINI_DENSE_CHARTS.has(chartType);

  const [axisRanges, setAxisRanges] = useState<{ x?: [number, number]; y?: [number, number] }>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRelayout = useCallback((e: any) => {
    const next: { x?: [number, number]; y?: [number, number] } = {};
    if (e['xaxis.range[0]'] != null && e['xaxis.range[1]'] != null)
      next.x = [Number(e['xaxis.range[0]']), Number(e['xaxis.range[1]'])];
    if (e['yaxis.range[0]'] != null && e['yaxis.range[1]'] != null)
      next.y = [Number(e['yaxis.range[0]']), Number(e['yaxis.range[1]'])];
    if (e['xaxis.autorange'] || e['yaxis.autorange'])
      setAxisRanges({});
    else if (next.x || next.y)
      setAxisRanges((prev) => ({ ...prev, ...next }));
  }, []);

  const data = useMemo(() => {
    const xField = xAxis.field;
    const yField = yAxis.field;
    return rawData
      .map((row) => {
        const xRaw = row[xField];
        const yRaw = row[yField];
        const xNum = xRaw != null ? Number(xRaw) : NaN;
        const yNum = yRaw != null ? Number(yRaw) : NaN;
        if (isNaN(xNum) || isNaN(yNum)) return null;
        if (xRaw === xNum && yRaw === yNum) return row;
        return { ...row, [xField]: xNum, [yField]: yNum };
      })
      .filter((r): r is Record<string, unknown> => r !== null);
  }, [rawData, xAxis.field, yAxis.field]);

  const pal = getPalette(palette);
  const bgColor = theme === 'dark' ? '#161A18' : '#FFFFFF';
  const textColor = theme === 'dark' ? '#A3ADA7' : '#4A524D';
  const gridColor = theme === 'dark' ? '#2A302C' : '#E6E9E7';
  const annotColor = theme === 'dark' ? '#FF6B6B88' : '#E5484D88';

  const pad = plotPadding;
  const bottomMargin = !mini && showLegend && legendPosition === 'bottom' ? pad + 45 : pad + 20;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const baseLayout: Record<string, any> = {
    autosize: !mini,
    width: mini ? miniWidth : undefined,
    height: mini ? miniHeight : undefined,
    uirevision: 'stable',
    paper_bgcolor: bgColor,
    plot_bgcolor: bgColor,
    font: { color: textColor, size: mini ? MINI_FONT_SIZE : 11, family: 'var(--fc-font-family)' },
    margin: mini ? MINI_MARGIN : { t: title ? pad + 10 : pad, r: pad, b: bottomMargin, l: pad + 20 },
    showlegend: mini ? false : showLegend,
    hovermode: mini ? false : 'closest',
    dragmode: mini ? false : undefined,
    ...(!mini && { transition: { duration: 300, easing: 'cubic-in-out' } }),
  };

  if (!mini && showLegend) {
    const lp = legendPosition;
    // Position legend in margin with anchor so it never overlaps the plot
    if (lp === 'bottom') baseLayout.legend = { orientation: 'h', y: -0.35, yanchor: 'top' };
    else if (lp === 'top') baseLayout.legend = { orientation: 'h', y: 1.2, yanchor: 'bottom' };
    else if (lp === 'left') baseLayout.legend = { x: -0.22, y: 0.5, xanchor: 'right' };
    else if (lp === 'right') baseLayout.legend = { x: 1.05, y: 0.5, xanchor: 'left' };
  }

  const xAxisObj: Record<string, unknown> = {
    title: mini ? undefined : { text: xAxis.label || xAxis.field },
    gridcolor: !mini && showGrid ? gridColor : 'transparent',
    type: xAxis.scale === 'log' ? 'log' : undefined,
    autorange: xAxis.scale === 'reversed' ? 'reversed' : undefined,
    range: (xAxis.min != null && xAxis.max != null) ? [xAxis.min, xAxis.max] : undefined,
    zerolinecolor: gridColor,
    tickangle: xAxisTickAngle || undefined,
    tickfont: mini ? { size: MINI_FONT_SIZE } : undefined,
    fixedrange: mini,
  };
  const yAxisObj: Record<string, unknown> = {
    title: mini ? undefined : { text: yAxis.label || yAxis.field },
    gridcolor: !mini && showGrid ? gridColor : 'transparent',
    type: yAxis.scale === 'log' ? 'log' : undefined,
    autorange: yAxis.scale === 'reversed' ? 'reversed' : undefined,
    range: (yAxis.min != null && yAxis.max != null) ? [yAxis.min, yAxis.max] : undefined,
    zerolinecolor: gridColor,
    tickangle: yAxisTickAngle || undefined,
    tickfont: mini ? { size: MINI_FONT_SIZE } : undefined,
    fixedrange: mini,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let traces: any[] = [];
  const shapes: Record<string, unknown>[] = [];
  const pltMarker = PLOTLY_MARKER_MAP[markerShape] || 'circle';
  const resolvedColorScale = invertColorScale ? `${colorScale}_r` : colorScale;

  const colorMap = resolveColorMapping(data, config.colorMapping ?? createDefaultMapping(), pal);
  const sizeMap = resolveSizeMapping(data, config.sizeMapping ?? createDefaultMapping(), markerSize);
  const shapeMap = resolveShapeMapping(data, config.shapeMapping ?? createDefaultMapping(), markerShape);

  let slopeForAnnotation: number | null = null;
  let slopeLabelClAlpha = false;

  function buildRowIdx(rows: Record<string, unknown>[]): number[] {
    return rows.map((r) => data.indexOf(r));
  }

  switch (chartType) {
    case 'line':
    case 'scatter': {
      const groups = groupData(data, groupBy);
      const xLbl = xAxis.label || xAxis.field;
      const yLbl = yAxis.label || yAxis.field;
      const xIsDeg = /\bdeg\b/i.test(xLbl) || /\bdeg\b/i.test(xAxis.field);
      const xBase = xLbl.replace(/\s*\(.*?\)/g, '').trim();
      const yBase = yLbl.replace(/\s*\(.*?\)/g, '').trim();

      let hovertemplate: string | undefined;
      if (!mini) {
        hovertemplate = `${xLbl}: %{x:.4g}<br>${yLbl}: %{y:.6g}<br>d(${yBase})/d(${xBase}): %{customdata[1]:.4f}`;
        if (xIsDeg) hovertemplate += ` /deg (%{customdata[2]:.4f} /rad)`;
        hovertemplate += '<extra></extra>';
      }

      let idx = 0;
      for (const [key, rows] of groups) {
        const rowIndices = buildRowIdx(rows);
        const baseColor = getColor(pal, key, idx);
        const xs = rows.map((r) => r[xAxis.field] as number);
        const ys = rows.map((r) => r[yAxis.field] as number);
        const grads = computeLocalGradients(xs, ys);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const markerObj: Record<string, any> = {
          color: colorMap ? rowIndices.map((i) => colorMap.perPoint[i]) : baseColor,
          size: sizeMap ? rowIndices.map((i) => sizeMap.perPoint[i]) : markerSize,
          symbol: shapeMap ? rowIndices.map((i) => shapeMap.perPoint[i]) : pltMarker,
          opacity: markerOpacity,
        };
        if (colorMap?.continuous) {
          markerObj.colorscale = resolvedColorScale;
          markerObj.showscale = !denseMiniChart;
          markerObj.colorbar = denseMiniChart ? undefined : { title: { text: config.colorMapping?.field ?? '' }, thickness: 12, len: 0.6 };
          if (colorMap.range) { markerObj.cmin = colorMap.range[0]; markerObj.cmax = colorMap.range[1]; }
        }
        if (sizeMap) { markerObj.sizemode = 'diameter'; }

        const customdata = rows.map((r, i) => {
          const g = grads[i]!;
          return xIsDeg
            ? [r._node_id, g, g * 180 / Math.PI]
            : [r._node_id, g];
        });

        traces.push({
          x: xs, y: ys,
          customdata,
          hovertemplate,
          mode: chartType === 'line' ? 'lines+markers' : 'markers',
          type: 'scatter', name: key,
          marker: markerObj,
          line: { color: baseColor, width: lineWidth, dash: lineStyle === 'dashdot' ? 'dashdot' : lineStyle },
          text: !mini && showDataLabels ? ys.map((v) => String(v)) : undefined,
          textposition: !mini && showDataLabels ? 'top center' : undefined,
        });
        if ((trendLine === 'linear' || showLiftCurveSlope) && xs.length > 1) {
          const { slope, intercept } = linearRegression(xs, ys);
          if (idx === 0) {
            slopeForAnnotation = slope;
            slopeLabelClAlpha = isClAlphaAxes(xAxis.field, yAxis.field);
          }
          const sortedX = [...xs].sort((a, b) => a - b);
          const trendColor = theme === 'dark' ? '#94a3b8' : '#64748b';
          traces.push({
            x: [sortedX[0], sortedX[sortedX.length - 1]],
            y: [slope * sortedX[0]! + intercept, slope * sortedX[sortedX.length - 1]! + intercept],
            mode: 'lines', type: 'scatter', name: `${key} (trend)`,
            line: { color: trendColor, width: 2, dash: 'dash' },
            showlegend: false,
            hoverinfo: 'skip',
          });
        }
        idx++;
      }
      Object.assign(baseLayout, { xaxis: xAxisObj, yaxis: yAxisObj });
      break;
    }

    case 'area': {
      const groups = groupData(data, groupBy);
      let idx = 0;
      for (const [key, rows] of groups) {
        const color = getColor(pal, key, idx);
        traces.push({
          x: rows.map((r) => r[xAxis.field]),
          y: rows.map((r) => r[yAxis.field]),
          fill: idx === 0 ? 'tozeroy' : 'tonexty',
          type: 'scatter', mode: 'lines', name: key,
          line: { color, width: lineWidth, dash: lineStyle },
          fillcolor: `${color}33`,
        });
        idx++;
      }
      Object.assign(baseLayout, { xaxis: xAxisObj, yaxis: yAxisObj });
      break;
    }

    case 'bubble': {
      const groups = groupData(data, groupBy);
      let idx = 0;
      const sizeField = config.sizeField;
      for (const [key, rows] of groups) {
        const rowIndices = buildRowIdx(rows);
        const baseColor = getColor(pal, key, idx);
        const sizes = sizeMap
          ? rowIndices.map((i) => sizeMap.perPoint[i])
          : sizeField
            ? rows.map((r) => Math.max(4, Number(r[sizeField]) || markerSize))
            : rows.map(() => markerSize);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const markerObj: Record<string, any> = {
          color: colorMap ? rowIndices.map((i) => colorMap.perPoint[i]) : baseColor,
          size: sizes, opacity: markerOpacity,
          symbol: shapeMap ? rowIndices.map((i) => shapeMap.perPoint[i]) : pltMarker,
          sizemode: 'diameter',
        };
        if (colorMap?.continuous) {
          markerObj.colorscale = resolvedColorScale;
          markerObj.showscale = !denseMiniChart;
          markerObj.colorbar = denseMiniChart ? undefined : { title: { text: config.colorMapping?.field ?? '' }, thickness: 12, len: 0.6 };
          if (colorMap.range) { markerObj.cmin = colorMap.range[0]; markerObj.cmax = colorMap.range[1]; }
        }
        traces.push({
          x: rows.map((r) => r[xAxis.field]),
          y: rows.map((r) => r[yAxis.field]),
          customdata: rows.map((r) => r._node_id as string | undefined),
          mode: 'markers', type: 'scatter', name: key,
          marker: markerObj,
        });
        idx++;
      }
      Object.assign(baseLayout, { xaxis: xAxisObj, yaxis: yAxisObj });
      break;
    }

    case 'bar': {
      const groups = groupData(data, groupBy);
      let idx = 0;
      for (const [key, rows] of groups) {
        const color = getColor(pal, key, idx);
        traces.push({
          x: rows.map((r) => r[xAxis.field]),
          y: rows.map((r) => r[yAxis.field]),
          type: 'bar', name: key,
          marker: { color, opacity: barOpacity },
          text: !mini && showDataLabels ? rows.map((r) => String(r[yAxis.field])) : undefined,
          textposition: !mini && showDataLabels ? 'outside' : undefined,
        });
        idx++;
      }
      Object.assign(baseLayout, {
        barmode: barMode, xaxis: xAxisObj, yaxis: yAxisObj,
      });
      break;
    }

    case 'histogram': {
      const groups = groupData(data, groupBy);
      let idx = 0;
      for (const [key, rows] of groups) {
        const color = getColor(pal, key, idx);
        traces.push({
          x: rows.map((r) => r[xAxis.field]),
          type: 'histogram', name: key, nbinsx: histBins,
          marker: { color, opacity: barOpacity },
        });
        idx++;
      }
      Object.assign(baseLayout, {
        barmode: barMode === 'group' ? 'overlay' : barMode,
        xaxis: { ...xAxisObj, title: mini ? undefined : { text: xAxis.label || xAxis.field } },
        yaxis: { ...yAxisObj, title: mini ? undefined : { text: 'Count' } },
      });
      break;
    }

    case 'box': {
      const groups = groupData(data, groupBy);
      let idx = 0;
      for (const [key, rows] of groups) {
        const color = getColor(pal, key, idx);
        traces.push({
          y: rows.map((r) => r[yAxis.field]),
          type: 'box', name: key,
          marker: { color }, boxpoints: 'outliers',
        });
        idx++;
      }
      Object.assign(baseLayout, { yaxis: yAxisObj });
      break;
    }

    case 'violin': {
      const groups = groupData(data, groupBy);
      let idx = 0;
      for (const [key, rows] of groups) {
        const color = getColor(pal, key, idx);
        traces.push({
          y: rows.map((r) => r[yAxis.field]),
          type: 'violin', name: key,
          line: { color }, box: { visible: true }, meanline: { visible: true },
        });
        idx++;
      }
      Object.assign(baseLayout, { yaxis: yAxisObj });
      break;
    }

    case 'polar': {
      const groups = groupData(data, groupBy);
      let idx = 0;
      for (const [key, rows] of groups) {
        const color = getColor(pal, key, idx);
        traces.push({
          r: rows.map((r) => r[yAxis.field]),
          theta: rows.map((r) => r[xAxis.field]),
          type: 'scatterpolar', mode: 'lines+markers', name: key,
          marker: { color, size: markerSize, symbol: pltMarker },
          line: { color, width: lineWidth },
        });
        idx++;
      }
      Object.assign(baseLayout, {
        polar: {
          bgcolor: bgColor,
          radialaxis: { gridcolor: gridColor, linecolor: gridColor },
          angularaxis: { gridcolor: gridColor, linecolor: gridColor },
        },
      });
      break;
    }

    case 'radar': {
      const groups = groupData(data, groupBy);
      const categories = mergedNumericFieldsFromData(data);
      let idx = 0;
      for (const [key, rows] of groups) {
        const color = getColor(pal, key, idx);
        const vals = categories.map((cat) => {
          const sum = rows.reduce((a, r) => a + Number(r[cat] ?? 0), 0);
          return sum / rows.length;
        });
        traces.push({
          r: [...vals, vals[0]], theta: [...categories, categories[0]],
          type: 'scatterpolar', mode: 'lines+markers', name: key, fill: 'toself',
          marker: { color, size: markerSize }, line: { color, width: lineWidth },
          fillcolor: `${color}22`,
        });
        idx++;
      }
      Object.assign(baseLayout, {
        polar: {
          bgcolor: bgColor,
          radialaxis: { visible: true, gridcolor: gridColor },
          angularaxis: { gridcolor: gridColor },
        },
      });
      break;
    }

    case 'contour': {
      const xVals = data.map((r) => Number(r[xAxis.field]));
      const yVals = data.map((r) => Number(r[yAxis.field]));
      const zField = zAxis?.field || yAxis.field;
      const zVals = data.map((r) => Number(r[zField]));
      traces.push({
        x: xVals, y: yVals, z: zVals,
        type: 'contour',
        colorscale: resolvedColorScale,
        ncontours: contourLevels,
        contours: {
          coloring: 'heatmap',
          showlabels: !mini,
          ...(contourSmooth ? {} : { smoothing: 0 }),
        },
        colorbar: denseMiniChart ? undefined : { title: { text: zField } },
      });
      Object.assign(baseLayout, { xaxis: xAxisObj, yaxis: yAxisObj });
      break;
    }

    case 'heatmap': {
      const xVals = data.map((r) => Number(r[xAxis.field]));
      const yVals = data.map((r) => Number(r[yAxis.field]));
      const zField = zAxis?.field || yAxis.field;
      const zVals = data.map((r) => Number(r[zField]));
      traces.push({
        x: xVals, y: yVals, z: zVals,
        type: 'heatmap',
        colorscale: resolvedColorScale,
        colorbar: denseMiniChart ? undefined : { title: { text: zField } },
      });
      Object.assign(baseLayout, { xaxis: xAxisObj, yaxis: yAxisObj });
      break;
    }

    case 'surface3d': {
      const zField = zAxis?.field || yAxis.field;
      const xUnique = [...new Set(data.map((r) => Number(r[xAxis.field])))].sort((a, b) => a - b);
      const yUnique = [...new Set(data.map((r) => Number(r[yAxis.field])))].sort((a, b) => a - b);
      const zMatrix: (number | null)[][] = yUnique.map((yv) =>
        xUnique.map((xv) => {
          const match = data.find((r) => Number(r[xAxis.field]) === xv && Number(r[yAxis.field]) === yv);
          return match ? Number(match[zField]) : null;
        }),
      );
      traces.push({
        x: xUnique, y: yUnique, z: zMatrix,
        type: 'surface', colorscale: resolvedColorScale,
        colorbar: denseMiniChart ? undefined : { title: { text: zField } },
      });
      Object.assign(baseLayout, {
        scene: {
          xaxis: {
            title: mini ? '' : xAxis.label || xAxis.field,
            gridcolor: mini ? 'transparent' : gridColor,
            backgroundcolor: bgColor,
            showticklabels: !mini,
          },
          yaxis: {
            title: mini ? '' : yAxis.label || yAxis.field,
            gridcolor: mini ? 'transparent' : gridColor,
            backgroundcolor: bgColor,
            showticklabels: !mini,
          },
          zaxis: {
            title: mini ? '' : zField,
            gridcolor: mini ? 'transparent' : gridColor,
            backgroundcolor: bgColor,
            showticklabels: !mini,
          },
          bgcolor: bgColor,
          camera: mini ? { eye: { x: 1.2, y: 1.2, z: 0.8 } } : undefined,
        },
      });
      break;
    }

    case 'scatter3d': {
      const zField = zAxis?.field || yAxis.field;
      const groups = groupData(data, groupBy);
      let idx = 0;
      for (const [key, rows] of groups) {
        const rowIndices = buildRowIdx(rows);
        const baseColor = getColor(pal, key, idx);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const markerObj: Record<string, any> = {
          color: colorMap ? rowIndices.map((i) => colorMap.perPoint[i]) : baseColor,
          size: sizeMap ? rowIndices.map((i) => sizeMap.perPoint[i]) : markerSize,
          opacity: markerOpacity,
          symbol: shapeMap ? rowIndices.map((i) => shapeMap.perPoint[i]) : pltMarker,
        };
        if (colorMap?.continuous) {
          markerObj.colorscale = resolvedColorScale;
          markerObj.showscale = !denseMiniChart;
          markerObj.colorbar = denseMiniChart ? undefined : { title: { text: config.colorMapping?.field ?? '' }, thickness: 12, len: 0.6 };
          if (colorMap.range) { markerObj.cmin = colorMap.range[0]; markerObj.cmax = colorMap.range[1]; }
        }
        if (sizeMap) { markerObj.sizemode = 'diameter'; }
        traces.push({
          x: rows.map((r) => Number(r[xAxis.field])),
          y: rows.map((r) => Number(r[yAxis.field])),
          z: rows.map((r) => Number(r[zField])),
          type: 'scatter3d', mode: 'markers', name: key,
          marker: markerObj,
        });
        idx++;
      }
      Object.assign(baseLayout, {
        scene: {
          xaxis: {
            title: mini ? '' : xAxis.label || xAxis.field,
            gridcolor: mini ? 'transparent' : gridColor,
            backgroundcolor: bgColor,
            showticklabels: !mini,
          },
          yaxis: {
            title: mini ? '' : yAxis.label || yAxis.field,
            gridcolor: mini ? 'transparent' : gridColor,
            backgroundcolor: bgColor,
            showticklabels: !mini,
          },
          zaxis: {
            title: mini ? '' : zField,
            gridcolor: mini ? 'transparent' : gridColor,
            backgroundcolor: bgColor,
            showticklabels: !mini,
          },
          bgcolor: bgColor,
          camera: mini ? { eye: { x: 1.2, y: 1.2, z: 0.8 } } : undefined,
        },
      });
      break;
    }

    default:
      return <div style={{ padding: 20, color: textColor }}>Unsupported chart type: {chartType}</div>;
  }

  if (!mini && (showMean || showMedian)) {
    const allY = data.map((r) => Number(r[yAxis.field])).filter((v) => !isNaN(v));
    if (allY.length > 0) {
      if (showMean) {
        const mean = allY.reduce((a, b) => a + b, 0) / allY.length;
        shapes.push({
          type: 'line', y0: mean, y1: mean, x0: 0, x1: 1, xref: 'paper',
          line: { color: annotColor, width: 1.5, dash: 'dash' },
        });
      }
      if (showMedian) {
        const sorted = [...allY].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        const median = sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
        shapes.push({
          type: 'line', y0: median, y1: median, x0: 0, x1: 1, xref: 'paper',
          line: { color: theme === 'dark' ? '#6DB5DD88' : '#457B9D88', width: 1.5, dash: 'dot' },
        });
      }
    }
  }

  if (shapes.length > 0) baseLayout.shapes = shapes;

  if (!mini && showLiftCurveSlope && slopeForAnnotation != null) {
    baseLayout.annotations = [...(baseLayout.annotations || []), {
      xref: 'paper', yref: 'paper', x: 1, y: 1.02, xanchor: 'right',
      text: (slopeLabelClAlpha ? 'Lift curve slope: ' : 'Slope: ') + slopeForAnnotation.toFixed(4) + (slopeLabelClAlpha ? ' /deg' : ''),
      showarrow: false,
      font: { size: 11, color: textColor },
    }];
  }

  if (!mini && selectedNodeId && (chartType === 'scatter' || chartType === 'line' || chartType === 'scatter3d')) {
    const selRows = data.filter((r) => r._node_id === selectedNodeId);
    if (selRows.length > 0) {
      const selTrace: Record<string, unknown> = {
        x: selRows.map((r) => r[xAxis.field]),
        y: selRows.map((r) => r[yAxis.field]),
        mode: 'markers',
        type: chartType === 'scatter3d' ? 'scatter3d' : 'scatter',
        name: 'Selected',
        showlegend: false,
        hoverinfo: 'skip',
        marker: {
          color: '#FFD700',
          size: (markerSize || 8) + 6,
          symbol: 'circle',
          opacity: 1,
          line: { color: '#FFD700', width: 2 },
        },
      };
      if (chartType === 'scatter3d' && zAxis?.field) {
        selTrace.z = selRows.map((r) => Number(r[zAxis.field!]));
      }
      traces.push(selTrace);
    }
  }

  const dropped = rawData.length - data.length;

  if (!mini && title) {
    baseLayout.title = {
      text: title,
      font: { size: titleSize, color: textColor, weight: titleWeight === 'bold' ? 700 : titleWeight === 'light' ? 300 : 400 },
    };
  }

  const offScreenArrow = useMemo(() => {
    if (mini) return null;
    if (!selectedNodeId || !axisRanges.x || !axisRanges.y) return null;
    if (chartType !== 'scatter' && chartType !== 'line') return null;
    const selRow = data.find((r) => r._node_id === selectedNodeId);
    if (!selRow) return null;
    const px = Number(selRow[xAxis.field]);
    const py = Number(selRow[yAxis.field]);
    if (isNaN(px) || isNaN(py)) return null;
    const [xLo, xHi] = axisRanges.x;
    const [yLo, yHi] = axisRanges.y;
    if (px >= xLo && px <= xHi && py >= yLo && py <= yHi) return null;
    const cx = (xLo + xHi) / 2, cy = (yLo + yHi) / 2;
    const angle = Math.atan2(py - cy, px - cx);
    const margin = baseLayout.margin ?? { t: 30, r: 30, b: 50, l: 50 };
    const edgeX = Math.cos(angle) > 0 ? `calc(100% - ${margin.r + 24}px)` : `${margin.l + 8}px`;
    const edgeY = Math.sin(angle) > 0 ? `${margin.t + 8}px` : `calc(100% - ${margin.b + 24}px)`;
    const deg = -(angle * 180 / Math.PI) + 90;
    return { edgeX, edgeY, deg };
  }, [mini, selectedNodeId, axisRanges, data, xAxis.field, yAxis.field, chartType, baseLayout.margin]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleHover = useCallback((event: any) => {
    const pt = event.points?.[0];
    if (!pt) return;
    const cd = pt.customdata;
    const nodeId = (Array.isArray(cd) ? cd[0] : cd) as string | undefined;
    if (nodeId) {
      const row = data.find((r) => r._node_id === nodeId);
      if (row) ctxMenu.setHoveredPoint(nodeId, row);
    }
  }, [data, ctxMenu]);

  const handleUnhover = useCallback(() => {
    ctxMenu.clearHoveredPoint();
  }, [ctxMenu]);

  // Purge Plotly internals on unmount to free WebGL contexts and event listeners
  const plotContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = plotContainerRef.current;
    return () => {
      if (!el) return;
      const plotDiv = el.querySelector('.js-plotly-plot') as HTMLElement | null;
      if (plotDiv) {
        import('plotly.js').then((Plotly) => Plotly.purge(plotDiv)).catch(() => {});
      }
    };
  }, []);

  return (
    <div
      ref={plotContainerRef}
      style={{ width: mini ? miniWidth : '100%', height: mini ? miniHeight : '100%', position: 'relative', overflow: 'hidden' }}
      onContextMenu={mini ? undefined : ctxMenu.handleContextMenu}
    >
      <style>{FLASH_STYLE}</style>
      {!mini && dropped > 0 && (
        <div style={{
          position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
          fontSize: 10, color: theme === 'dark' ? '#FF6B6B' : '#E5484D',
        }}>
          {data.length} of {rawData.length} rows plotted — {dropped} rows missing {xAxis.field} or {yAxis.field}
        </div>
      )}
      {offScreenArrow && (
        <div
          style={{
            position: 'absolute', left: offScreenArrow.edgeX, top: offScreenArrow.edgeY, zIndex: 20,
            transform: `rotate(${offScreenArrow.deg}deg)`,
            color: '#FFD700', fontSize: 18, lineHeight: 1, pointerEvents: 'none',
            filter: 'drop-shadow(0 0 3px rgba(255,215,0,0.6))',
            animation: 'flash-point 1.2s ease-in-out infinite',
          }}
        >
          &#x25B2;
        </div>
      )}
      <Plot
        data={traces}
        layout={baseLayout as unknown as Plotly.Layout}
        useResizeHandler={!mini}
        style={{ width: mini ? miniWidth : '100%', height: mini ? miniHeight : '100%' }}
        config={{
          responsive: !mini,
          staticPlot: mini,
          displayModeBar: !mini,
          displaylogo: false,
          scrollZoom: !mini,
          toImageButtonOptions: { scale: config.dpi / 72 },
        }}
        onRelayout={mini ? undefined : handleRelayout}
        onHover={mini ? undefined : handleHover}
        onUnhover={mini ? undefined : handleUnhover}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={!mini && onPointClick ? (event: any) => {
          const cd = event.points?.[0]?.customdata;
          const nodeId = (Array.isArray(cd) ? cd[0] : cd) as string | undefined;
          if (nodeId) {
            const row = data.find((r) => r._node_id === nodeId);
            if (row) onPointClick(row);
          }
        } : undefined}
        onSelected={mini ? undefined : (event) => {
          if (!event?.points?.length) return;
          const nodeIds = event.points
            .map((p) => {
              const cd = (p as unknown as { customdata?: unknown }).customdata;
              return (Array.isArray(cd) ? cd[0] : cd) as string | undefined;
            })
            .filter((id): id is string => typeof id === 'string' && id.length > 0);
          if (nodeIds.length > 0) selectNodes(nodeIds);
        }}
        onDeselect={mini ? undefined : () => clearSelection()}
      />
      {ctxMenu.menu && <DataPointContextMenu menu={ctxMenu.menu} onClose={ctxMenu.close} />}
    </div>
  );
}
